/**
 * Central investment / onboarding eligibility rules.
 *
 * Regular / Free (isSubscriptionFree): KYC + onboarding only.
 * Prime / VIP and other non-Direct types: KYC + onboarding only (existing non-Direct path).
 *
 * Direct (paid) is onboarded first and charged after:
 *   onboarding → payment → Digio agreement → risk assessment.
 * Picking Direct does not write customerType to the server — that only
 * happens once Razorpay payment is confirmed (see PENDING_DIRECT_PLAN_KEY
 * below), so the customer is never actually marked DIRECT for free.
 *
 * Uses existing onboarding-status fields. Do not invent new API keys.
 */

const DIRECT_TYPE = 'DIRECT';

/**
 * customerType is only written server-side once Razorpay payment succeeds.
 * Between picking Direct and paying, this on-device marker stands in for it
 * everywhere eligibility is computed — without it, a customer who finishes
 * onboarding but never pays would read as having no plan at all once
 * `onboardingDone` is true, and `canInvest` would fall through to `true`
 * (nextAction stays null), letting them invest for free. Treating the pending
 * choice as DIRECT here keeps onboarding/payment gating identical to an
 * already-DIRECT customer until the real field lands.
 */
const PENDING_DIRECT_PLAN_KEY = 'pendingDirectPlanSelection';

export const markPendingDirectPlanSelection = () => {
  try {
    localStorage.setItem(PENDING_DIRECT_PLAN_KEY, 'true');
  } catch {
    // Best-effort only — worst case the customer is asked to pick Direct again.
  }
};

export const clearPendingDirectPlanSelection = () => {
  try {
    localStorage.removeItem(PENDING_DIRECT_PLAN_KEY);
  } catch {
    // Best-effort only.
  }
};

const hasPendingDirectPlanSelection = () => {
  try {
    return localStorage.getItem(PENDING_DIRECT_PLAN_KEY) === 'true';
  } catch {
    return false;
  }
};

const asObject = (value) =>
  value && typeof value === 'object' ? value : null;

export const getOnboardingStatusPayload = (onboarding) => {
  if (!asObject(onboarding)) return {};

  return (
    asObject(onboarding.onboardingStatus) ||
    asObject(onboarding.data) ||
    asObject(onboarding.result) ||
    onboarding
  );
};

const readSources = (onboarding) => {
  const status = getOnboardingStatusPayload(onboarding);
  const nested = asObject(status?.data);
  return [onboarding, status, nested].filter(Boolean);
};

const readFlag = (onboarding, key) =>
  readSources(onboarding).some((source) => source?.[key] === true);

const readNestedCompleted = (onboarding, keys) =>
  readSources(onboarding).some((source) =>
    keys.some((key) => source?.[key]?.completed === true)
  );

/** The type actually recorded on the server, ignoring any pending-Direct marker. */
export const getServerCustomerType = (onboarding) => {
  for (const source of readSources(onboarding)) {
    const type = source?.customerType || source?.customer_type;
    if (type) return type;
  }
  return null;
};

export const getCustomerType = (onboarding) => {
  const serverType = getServerCustomerType(onboarding);
  if (serverType) return serverType;
  // No server-recorded type yet — fall back to a pending Direct selection
  // that hasn't been paid for. A confirmed server type above always wins.
  return hasPendingDirectPlanSelection() ? DIRECT_TYPE : null;
};

export const isSubscriptionFree = (onboarding) =>
  readFlag(onboarding, 'isSubscriptionFree');

export const isDirectCustomer = (onboarding) =>
  getCustomerType(onboarding) === DIRECT_TYPE;

export const isOnboardingComplete = (onboarding) =>
  readFlag(onboarding, 'isOnboardingCompleted') &&
  readFlag(onboarding, 'isUCCOnboardingCompleted');

/** BSE/exchange-side registration. Survives a later KRA re-review, unlike isOnboardingCompleted. */
export const isUccOnboardingComplete = (onboarding) =>
  readFlag(onboarding, 'isUCCOnboardingCompleted');

/**
 * Existing investors keep transacting on folios they already hold.
 * A KYC/KRA re-review months or years after onboarding flips isOnboardingCompleted
 * back to false while the BSE UCC stays valid — that must not block
 * invest / redeem / switch / STP / SWP on a folio the customer already holds.
 */
export const canTransactAsExistingInvestor = (
  onboarding,
  { hasExistingHolding } = {},
) => hasExistingHolding === true && isUccOnboardingComplete(onboarding);

export const isRiskAssessmentComplete = (onboarding) =>
  readNestedCompleted(onboarding, ['riskAssessment', 'risk_assessment']);

/** Existing Digio completion field from onboarding-status: agreement.completed */
export const isAgreementComplete = (onboarding) =>
  readNestedCompleted(onboarding, ['agreement']);

export const isKycStepDone = (onboarding) =>
  readSources(onboarding).some(
    (source) =>
      source?.kyc?.completed === true || source?.kyc?.inReview === true,
  );

export const isBankLinkingComplete = (onboarding) =>
  readNestedCompleted(onboarding, ['bankLinking', 'bank_linking']);

export const isNomineeComplete = (onboarding) =>
  readNestedCompleted(onboarding, ['nominee']);

/**
 * BSE eLog — the last thing the customer signs on the account-setup screen, and
 * the only part of the BSE stage they act on (UCC registration and FATCA are
 * fired by the app itself). BSE flips uccElog.completed before the
 * account-level flags follow, so isUCCOnboardingCompleted standing true is
 * proof the eLog is already signed even when the per-step object is missing.
 */
export const isElogComplete = (onboarding) =>
  readNestedCompleted(onboarding, ['uccElog', 'ucc_elog']) ||
  isUccOnboardingComplete(onboarding);

/** KYC with KRA; bank + nominee done — user cannot action anything yet. */
export const isKycInReview = (onboarding) =>
  readSources(onboarding).some(
    (source) =>
      source?.kyc?.inReview === true &&
      source?.kyc?.completed !== true &&
      source?.bankLinking?.completed === true &&
      source?.nominee?.completed === true,
  );

/**
 * Direct extras (payment, Digio, mandatory RA) apply only to paid Direct customers.
 * Free subscription and Prime/VIP must not inherit Direct requirements.
 */
export const isDirectJourneyRequired = (onboarding) =>
  isDirectCustomer(onboarding) && !isSubscriptionFree(onboarding);

/**
 * Onboarding steps in the order the account-setup screen walks them, with the
 * route that opens each one. Steps completed from the status screen itself
 * (BSE registration / eLog) point back at that screen.
 */
const ONBOARDING_STEP_SEQUENCE = [
  {
    key: 'kyc',
    label: 'KYC Details',
    path: '/onboarding/kra-kyc',
    isComplete: (onboarding) => isKycStepDone(onboarding),
  },
  {
    key: 'bankLinking',
    label: 'Bank Account Linking',
    path: '/onboarding/bank-linking',
    isComplete: (onboarding) => isBankLinkingComplete(onboarding),
  },
  {
    key: 'nominee',
    label: 'Nominee Details',
    path: '/onboarding/nominee-details',
    isComplete: (onboarding) => isNomineeComplete(onboarding),
  },
  {
    key: 'uccElog',
    label: 'Elog',
    // How the step reads inside a sentence — the row is a call to action, but
    // "Finish Complete Elog to start investing" is not a sentence.
    nounLabel: 'the BSE eLog',
    path: '/onboarding/status',
    isComplete: (onboarding) => isElogComplete(onboarding),
  },
];

/** Marks the fallback below, so a prompt can tell it from a real step. */
export const ACCOUNT_SETUP_STEP_KEY = 'status';

/** Whatever is left over — the status screen lists every remaining step. */
const ACCOUNT_SETUP_STEP = {
  key: ACCOUNT_SETUP_STEP_KEY,
  label: 'Account Setup',
  path: '/onboarding/status',
};

/**
 * First onboarding step the customer still owes, so the "onboarding pending"
 * prompt can send them straight there instead of to the status screen.
 * Always returns a step (falling back to the status screen) so callers never
 * have to guess a destination.
 */
export const getPendingOnboardingStep = (onboarding) => {
  const pending = getOnboardingStepsProgress(onboarding).find(
    (step) => step.isPending === true,
  );

  if (!pending) return { ...ACCOUNT_SETUP_STEP };

  const { key, label, nounLabel, path } = pending;
  return { key, label, nounLabel, path };
};

/**
 * Every onboarding step with its status, so a prompt can show the customer
 * what is already done and what is left. Only the first incomplete step is
 * flagged `isPending` — the rest are still ahead of them.
 */
export const getOnboardingStepsProgress = (onboarding) => {
  let pendingSeen = false;

  return ONBOARDING_STEP_SEQUENCE.map(
    ({ key, label, nounLabel, path, isComplete }) => {
      const completed = isComplete(onboarding) === true;
      const isPending = !completed && !pendingSeen;

      if (isPending) pendingSeen = true;

      return { key, label, nounLabel, path, completed, isPending };
    },
  );
};

export const getOnboardingIncompleteSteps = (onboarding) => {
  const steps = [];
  if (!readFlag(onboarding, 'isOnboardingCompleted')) {
    steps.push('Onboarding');
  }
  if (!readFlag(onboarding, 'isUCCOnboardingCompleted')) {
    steps.push('UCC Onboarding');
  }
  return steps;
};

export const getInvestmentEligibility = (
  onboarding,
  { isSubscriptionActive, hasExistingHolding } = {},
) => {
  const customerType = getCustomerType(onboarding);
  const isFreeCustomer = isSubscriptionFree(onboarding);
  const isDirect = isDirectCustomer(onboarding);
  const existingInvestorBypass = canTransactAsExistingInvestor(onboarding, {
    hasExistingHolding,
  });
  const onboardingDone =
    isOnboardingComplete(onboarding) || existingInvestorBypass;
  const riskDone = isRiskAssessmentComplete(onboarding);
  const agreementDone = isAgreementComplete(onboarding);
  const directJourney = isDirect && !isFreeCustomer;
  const paid = isSubscriptionActive === true || isFreeCustomer;

  // Direct is onboarded before it is charged: KYC first, then pay, sign and
  // profile. Regular / Prime / free customers only ever surface 'onboarding'.
  let nextAction = null;
  if (directJourney && !onboardingDone) {
    nextAction = 'onboarding';
  } else if (directJourney && !paid) {
    nextAction = 'payment';
  } else if (directJourney && !agreementDone) {
    nextAction = 'agreement';
  } else if (directJourney && !riskDone) {
    nextAction = 'risk';
  } else if (!onboardingDone) {
    nextAction = 'onboarding';
  }

  return {
    customerType,
    isFreeCustomer,
    isDirect,
    isOnboardingComplete: onboardingDone,
    isExistingInvestorBypass: existingInvestorBypass,
    isRiskAssessmentComplete: riskDone,
    isAgreementComplete: agreementDone,
    isRiskAssessmentMandatory: directJourney,
    isAgreementMandatory: directJourney,
    shouldSkipPlanSelection: isFreeCustomer,
    shouldSkipPayment: isFreeCustomer || !isDirect,
    shouldSkipAgreement: !directJourney,
    canInvest: nextAction == null,
    nextAction,
  };
};

/** Normalize RTK / lazy-query onboarding responses to the status payload object. */
export const extractOnboardingStatusFromResponse = (response) => {
  if (!response) return null;

  const payload = getOnboardingStatusPayload(response);
  const hasStatusFields =
    payload?.customerType != null ||
    payload?.isOnboardingCompleted != null ||
    payload?.kyc != null;

  return hasStatusFields ? payload : null;
};

/**
 * Invest-click gate — paid Direct order:
 * onboarding → payment → agreement → risk.
 *
 * Browsing and plan selection stay open; onboarding leads the Direct journey and
 * the paid steps follow it, raised here or by the host layout's journey watcher.
 */
export const getInvestGateResult = (
  onboarding,
  { isSubscriptionActive, hasExistingHolding } = {},
) => {
  const eligibility = getInvestmentEligibility(onboarding, {
    isSubscriptionActive,
    hasExistingHolding,
  });

  const { nextAction } = eligibility;

  if (nextAction === 'onboarding') {
    return {
      allowed: false,
      action: 'onboarding',
      incompleteSteps: getOnboardingIncompleteSteps(onboarding),
      pendingStep: getPendingOnboardingStep(onboarding),
      onboardingSteps: getOnboardingStepsProgress(onboarding),
      eligibility,
    };
  }

  if (nextAction != null) {
    return { allowed: false, action: nextAction, eligibility };
  }

  return { allowed: true, action: null, eligibility };
};
